<template>
  <div class="grid-page-chart-editor">
    <div class="_top">
      <div class="_left">
        <breadcrumbs
          class="_breadcrumbs"
          :breadcrumbs="breadcrumbItems"
        />
        <div class="_divider">
          /
        </div>
        <m-dropdown
          :title="$t('general.actions')"
          class="_title"
        >
          <m-btn
            type="clickable"
            hide-border
            small
          >
            {{ maxLength(props.gridPageTile.title, 30) }}
          </m-btn>
          <template #overlay>
            <m-card
              padding-xxs
              :style="{ width: '20rem'}"
            >
              <m-focusable>
                <simple-editor
                  auto-focus
                  :initial-value="props.gridPageTile.title"
                  :read-only="false"
                  auto-select
                  :placeholder="$t('gridPageChartEditor.titlePlaceholder')"
                  @update:value="updateTitle"
                />
              </m-focusable>
            </m-card>
          </template>
        </m-dropdown>
        <m-dropdown
          v-model:value="descriptionEditMode"
          :style="{ marginLeft: '.6rem' }"
          :title="$t('general.actions')"
          class="_description"
        >
          <m-btn
            type="clickable"
            small
            light
            :icon="props.gridPageTile.description === '' ? 'plus' : undefined"
            hide-border
          >
            <template v-if="props.gridPageTile.description !== ''">
              {{ maxLength(props.gridPageTile.description, 50) }}
            </template>
            <template v-else>
              {{ $t('gridPageChartEditor.addDescription') }}
            </template>
          </m-btn>
          <template #overlay>
            <m-card
              padding-xxs
              :style="{ width: '30rem'}"
            >
              <m-focusable>
                <simple-editor
                  :style="{ width: '100%' }"
                  :initial-value="props.gridPageTile.description"
                  :read-only="false"
                  auto-select
                  auto-focus
                  :placeholder="$t('gridPageChartEditor.descriptionPlaceholder')"
                  :enter-handler="updateDescription"
                  @update:value="updateDescription"
                  @enter="descriptionEditMode = false"
                />
              </m-focusable>
            </m-card>
          </template>
        </m-dropdown>
      </div>
      <div class="_right">
        <template
          v-if="isDirty"
        >
          <m-btn
            class="_discard-btn"
            hide-border
            light
            @click="discard"
          >
            {{ $t('general.discardChanges') }}
          </m-btn>
          <m-btn
            color="primary"
            @click="save"
          >
            {{ $t('general.save') }}
          </m-btn>
        </template>
        <m-btn
          hide-border
          light
          icon="close"
          fab
          @click="handleClose"
        />
      </div>
    </div>
    <div class="_bottom">
      <div class="_left">
        <full-screen-spinner v-if="chartDataQuery.loading.value" />
        <component
          :is="effectiveChartType !== gridPageChartType.table ? DraggableLayout : 'div'"
          v-else
          v-model:position="tablePosition"
          class="_draggable-layout"
        >
          <div
            :class="['_chart-container', { '-table': effectiveChartType === gridPageChartType.table }]"
          >
            <m-content
              class="_chart-content"
              :padding-x="12"
              :padding-y="10"
            >
              <grid-page-chart
                :chart="toSave"
                :chart-data-query="chartDataQuery"
                size="large"
              />
            </m-content>
          </div>
          <template #bottom>
            <m-content
              :padding-x="12"
              :padding-top="0"
              :padding-bottom="10"
              class="_table-container"
            >
              <grid-page-chart-data-table
                :chart="toSave"
                :chart-data-query="chartDataQuery"
              />
            </m-content>
          </template>
        </component>
      </div>
      <div class="_right">
        <m-section
          :title="$t('gridPageChartEditor.title')"
          heading-size="h5"
        >
          <m-form-item :label="$t('gridPageChartEditor.source')">
            <m-select
              v-model:value="source"
              :items="sources"
              full-width
            />
          </m-form-item>
          <m-form-item :label="$t('gridPageChartEditor.filter')">
            <m-dropdown
              block
              :relocate-key="filter"
              :title="$t('general.actions')"
            >
              <m-focusable
                type="clickable"
                full-width
              >
                <m-tag
                  icon="filter"
                  :title="$t('gridPageChartEditor.filter')"
                  color="none"
                />
                <m-tag
                  v-if="filter?.children.length > 0"
                  :title="`${filter?.children.length}`"
                  small
                  :style="{ marginLeft: '.6rem' }"
                />
              </m-focusable>
              <template #overlay>
                <m-card
                  padding-xs
                  :style="{ minWidth: '30rem' }"
                >
                  <scope-filter
                    v-model:value="filter"
                    can-remove-all
                    allow-grouping
                    :props="filterProps"
                    :account="loggedInUserAccount"
                  />
                </m-card>
              </template>
            </m-dropdown>
          </m-form-item>
          <m-form-item
            v-if="source === goalConfig.model"
            :label="$t('gridPageChartEditor.cycle')"
          >
            <goal-cycle-selector
              v-model:value="selectedCycles"
              keep-open-on-click
              multiple
              full-width
              wrap
            />
          </m-form-item>
        </m-section>
        <m-section
          :title="$t('gridPageChartEditor.chart')"
          heading-size="h5"
        >
          <m-form-item class="_chart-type-selector">
            <m-tooltip
              v-for="type in chartTypes"
              :key="type"
            >
              <m-focusable
                type="clickable"
                :class="{ '_chart-btn': true, '-selected': effectiveChartType === type, '-disabled': isTimeSeriesMode && type !== gridPageChartType.line }"
                :disabled="isTimeSeriesMode && type !== gridPageChartType.line"
                :hide-hover="effectiveChartType === type"
                @click="!isTimeSeriesMode && (chartType = type)"
              >
                <div class="_icon-wrapper">
                  <m-icon
                    :type="getIconByType(type)"
                    size="24"
                    :color="isTimeSeriesMode && type !== gridPageChartType.line ? $colors.grey.lighten2 : effectiveChartType === type ? $colors.blue.base : $colors.grey.lighten1"
                  />
                </div>
              </m-focusable>
              <template #title>
                {{ isTimeSeriesMode && type !== gridPageChartType.line ? $t('gridPageChartEditor.chartTypeDisabledForTimeSeries') : $t(`gridPageChartEditor.${type}`) }}
              </template>
            </m-tooltip>
          </m-form-item>
        </m-section>
        <m-alert
          v-if="!canGroupByUser && source === goalConfig.model"
          type="info"
          class="_user-grouping-hint"
          hide-border
        >
          {{ $t('gridPageChartEditor.userGroupingDisabled') }}
        </m-alert>
        <!-- Metric (Y-axis) section - shown first for all chart types -->
        <template v-if="[gridPageChartType.bar, gridPageChartType.column, gridPageChartType.line, gridPageChartType.table].includes(effectiveChartType)">
          <m-section
            :title="$t('gridPageChartEditor.metric')"
            heading-size="h5"
          >
            <m-form-item :label="$t('gridPageChartEditor.whatToShow')">
              <aggregation-selector
                v-model:value="yAxisGroupBy"
                :properties="properties"
                :show-time-series-options="source === goalConfig.model"
                full-width
              />
            </m-form-item>
            <template v-if="!isTimeSeriesMode">
              <m-form-item :label="$t('gridPageChartEditor.groupBy')">
                <group-by-selector
                  v-model:value="stackOptions"
                  :properties="properties"
                  show-none
                  full-width
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                />
              </m-form-item>
              <m-form-item
                v-if="stackOptions.groupBy === chartGroupByOption.bucket"
                :label="$t('gridPageChartEditor.bucket')"
              >
                <bucket-selector
                  v-model:value="stackCustomBucket"
                />
              </m-form-item>
            </template>
          </m-section>
          <m-section
            v-if="isTimeSeriesMode"
            :title="$t('gridPageChartEditor.timeRange')"
            heading-size="h5"
          >
            <m-form-item :label="$t('gridPageChartEditor.interval')">
              <m-select
                v-model:value="selectedInterval"
                :items="timeSeriesIntervalOptions"
                full-width
              />
            </m-form-item>
          </m-section>
          <m-section
            v-else
            :title="$t('gridPageChartEditor.groupBySection')"
            heading-size="h5"
          >
            <template v-if="[gridPageChartType.column, gridPageChartType.line, gridPageChartType.table].includes(effectiveChartType)">
              <m-form-item :label="$t('gridPageChartEditor.whatToShow')">
                <group-by-selector
                  v-model:value="xAxisGroupBy"
                  :properties="properties"
                  full-width
                  show-search
                  match-trigger-width
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                />
              </m-form-item>
              <m-form-item
                v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
                :label="$t('gridPageChartEditor.bucket')"
              >
                <bucket-selector
                  v-model:value="customBucket"
                />
              </m-form-item>
              <m-form-item
                v-if="showExcludeNullValues"
              >
                <m-focusable
                  type="clickable"
                  @click="toggleExcludeNullValues"
                >
                  <m-switch
                    :value="excludeNullValues"
                    :label="$t('gridPageChartEditor.excludeNullValues')"
                    small
                  />
                </m-focusable>
              </m-form-item>
            </template>
            <template v-else>
              <m-form-item :label="$t('gridPageChartEditor.whatToShow')">
                <group-by-selector
                  v-model:value="xAxisGroupBy"
                  :properties="properties"
                  full-width
                  show-search
                  match-trigger-width
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                />
              </m-form-item>
              <m-form-item
                v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
                :label="$t('gridPageChartEditor.bucket')"
              >
                <bucket-selector
                  v-model:value="customBucket"
                />
              </m-form-item>
              <m-form-item
                v-if="showExcludeNullValues"
              >
                <m-focusable
                  type="clickable"
                  @click="toggleExcludeNullValues"
                >
                  <m-switch
                    :value="excludeNullValues"
                    :label="$t('gridPageChartEditor.excludeNullValues')"
                    small
                  />
                </m-focusable>
              </m-form-item>
            </template>
          </m-section>
        </template>
        <template v-if="effectiveChartType === gridPageChartType.pie">
          <m-section
            :title="$t('gridPageChartEditor.data')"
            heading-size="h5"
          >
            <m-form-item :label="$t('gridPageChartEditor.whatToShow')">
              <group-by-selector
                v-model:value="xAxisGroupBy"
                :properties="properties"
                :hidden-group-by-options="[chartGroupByOption.unique]"
                full-width
                show-search
                match-trigger-width
              />
            </m-form-item>
            <m-form-item
              v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
              :label="$t('gridPageChartEditor.bucket')"
            >
              <bucket-selector
                v-model:value="customBucket"
              />
            </m-form-item>
            <m-form-item :label="$t('gridPageChartEditor.representation')">
              <aggregation-selector
                v-model:value="yAxisGroupBy"
                :properties="properties"
                :show-time-series-options="source === goalConfig.model"
                full-width
              />
            </m-form-item>
          </m-section>
        </template>
        <template v-if="effectiveChartType === gridPageChartType.metric">
          <m-section
            :title="$t('gridPageChartEditor.data')"
            heading-size="h5"
          >
            <m-form-item :label="$t('gridPageChartEditor.whatToShow')">
              <aggregation-selector
                v-model:value="yAxisGroupBy"
                :properties="properties"
                :show-time-series-options="source === goalConfig.model"
                full-width
              />
            </m-form-item>
          </m-section>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import AggregationSelector from '@/components/custom-grid/AggregationSelector.vue';
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs.vue';
import BucketSelector from '@/components/custom-grid/BucketSelector.vue';
import DraggableLayout from '@/components/custom-grid/chart/DraggableLayout.vue';
import FullScreenSpinner from 'shared/components/FullScreenSpinner.vue';
import GoalCycleSelector from '@/components/goal/cycle/GoalCycleSelector.vue';
import GridPageChart from '@/components/custom-grid/chart/GridPageChart.vue';
import GridPageChartDataTable from '@/components/custom-grid/GridPageChartDataTable.vue';
import GroupBySelector from '@/components/custom-grid/GroupBySelector.vue';
import ScopeFilter from '@/components/filter/ScopeFilter.vue';
import SimpleEditor from '@/components/SimpleEditor.vue';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useChartDataQuery from '@/composables/chart-data-query';
import useConfirmDialog from '@/composables/confirm-dialog';
import useDebounce from 'shared/composables/debounce';
import useGoalProperty from '@/composables/property/goal-property';
import useGridPage from '@/composables/grid-page/grid-page';
import useGridPageTileChart from '@/composables/grid-page/chart/grid-page-tile-chart';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useSnackbar from '@/composables/snackbar';
import { CREATED_AT, MODIFIED_AT } from 'shared/api/query/constants';
import { TIME_SERIES_AGGREGATORS } from '@/lib/constants';
import { aggregatorType, chartGroupByOption, gridPageChartType, propertyType, routeName, timeSeriesInterval } from 'shared/constants.json';
import { canFillEmptyValues, isEqualGridPageTileChart } from '@/composables/grid-page/chart/utils';
import { computed, ref, watch } from 'vue';
import { createPropsList } from '@/lib/props';
import { directProperties as directGoalActivityProperties } from '@/lib/goal-activity/properties';
import { directProperties as directUpdateProperties } from '@/lib/updates/properties';
import { goalActivity as goalActivityConfig, goal as goalConfig, update as updateConfig } from 'shared/api/query/configs.json';
import { goalChartProperties } from '@/lib/goal/properties';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  gridPageTile: {
    type: Object,
    required: true,
  },
  gridPage: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const breadcrumbItems = ref([
  { title: t('views.dashboards'), to: { name: routeName.dashboards } },
  { title: props.gridPage.title, onClick: () => { emit('close'); } },
]);

const debounceSvc = useDebounce();
const gridPageService = useGridPage();

const getIconByType = (type) => {
  switch (type) {
    case gridPageChartType.bar:
      return 'horizontal-bar-chart';
    case gridPageChartType.column:
      return 'vertical-bar-chart';
    case gridPageChartType.line:
      return 'line-chart-colored';
    case gridPageChartType.pie:
      return 'pie-chart-colored';
    case gridPageChartType.metric:
      return 'number';
    case gridPageChartType.table:
      return 'chart-table';
    default:
      return '';
  }
};

function updateTitle(val) {
  debounceSvc.debounce(() => gridPageService.updateGridPageTile({ uid: props.gridPageTile.uid, title: val }), 500);
}

const descriptionEditMode = ref(false);
function updateDescription(val) {
  debounceSvc.debounce(() => gridPageService.updateGridPageTile({ uid: props.gridPageTile.uid, description: val }), 500);
}

const chartService = useGridPageTileChart();
const gridPageSvc = useGridPage();
const gridPageTileChart = computed(() => {
  const res = gridPageSvc.gridPageTileChart.value.find((e) => e.tile.uid === props.gridPageTile.uid);
  if (res === undefined) {
    return null;
  }

  return res;
});

const tablePosition = ref('center');

const snackbarService = useSnackbar();

const save = () => {
  chartService.updateSingle(toSave.value).then(() => {
    snackbarService.success(t('success.saved'));
  });
};
const confirmDialog = useConfirmDialog();

const handleClose = () => {
  if (isDirty.value) {
    confirmDialog.confirm({
      title: t('gridPageChartEditor.closePrompt'),
      okText: t('general.yesDiscard'),
      okType: 'warning',
      cancelText: t('general.cancel'),
      onOk: () => {
        init();
        emit('close');
      },
    });
    return;
  }
  emit('close');
};

const discard = () => {
  init();
};

const toSave = computed(() => ({
  uid: gridPageTileChart.value.uid,
  source: source.value,
  chartType: effectiveChartType.value,
  filter: filter.value,
  goalCycles: selectedCycles.value,
  groupBy: {
    uid: gridPageTileChart.value.groupBy.uid,
    ...groupByToStore.value,
    customBucket: {
      uid: gridPageTileChart.value.groupBy.customBucket.uid,
      ...customBucket.value,
    },
  },
  aggregationConfig: {
    uid: aggregationConfig.value.uid,
    aggregation: {
      uid: aggregationConfig.value.aggregation.uid,
      ...yAxisGroupBy.value,
    },
    stackOptions: {
      uid: aggregationConfig.value.stackOptions.uid,
      ...stackOptionsToStore.value,
      customBucket: {
        uid: gridPageTileChart.value.aggregationConfig.stackOptions.customBucket.uid,
        ...stackCustomBucket.value,
      },
    },
    timeSeriesInterval: selectedInterval.value,
  },
  excludeNullValues: excludeNullValues.value,
}));

const stackOptionsToStore = computed(() => {
  if ([gridPageChartType.pie, gridPageChartType.metric].includes(effectiveChartType.value)) {
    return { groupBy: chartGroupByOption.none };
  }
  if (isTimeSeriesMode.value) {
    return { groupBy: chartGroupByOption.none };
  }
  return stackOptions.value;
});

const groupByToStore = computed(() => {
  if (effectiveChartType.value === gridPageChartType.metric) {
    return { groupBy: chartGroupByOption.none };
  }
  if (isTimeSeriesMode.value) {
    return { groupBy: chartGroupByOption.none };
  }
  return xAxisGroupBy.value;
});

const aggregationConfig = computed(() => gridPageTileChart.value.aggregationConfig);

const maxLength = (str, max) => {
  if (str.length === 0) {
    return t('general.untitled');
  }
  if (str.length > max) {
    return `${str.slice(0, max)}...`;
  }
  return str;
};

const source = ref(goalConfig.model);

const { loggedInUserAccount } = useLoggedInUserAccount();

const filter = ref(null);

const { properties: goalProperties } = useGoalProperty();
const { accountSettings } = useAccountSettings();
const canGroupByUser = computed(() => accountSettings.value?.canGroupByUser ?? false);

const selectedCycles = ref([]);

const chartTypes = [gridPageChartType.column, gridPageChartType.bar, gridPageChartType.line, gridPageChartType.pie, gridPageChartType.metric, gridPageChartType.table];
const chartType = ref(gridPageChartType.bar);

const xAxisGroupBy = ref(null);
const customBucket = ref(null);
const stackCustomBucket = ref(null);
const properties = computed(() => {
  const isUserProperty = (prop) => prop.isProperty && prop.property?.type === propertyType.user;
  const filterUser = (list) => (canGroupByUser.value ? list : list.filter((p) => !isUserProperty(p)));

  switch (source.value) {
    case goalConfig.model:
      return filterUser([
        { isProperty: false, edge: CREATED_AT, edgeType: propertyType.date },
        { isProperty: false, edge: MODIFIED_AT, edgeType: propertyType.date },
        { isProperty: false, edge: goalConfig.edges.cachedCalculatedCurrent, edgeType: propertyType.number },
        { isProperty: false, edge: goalConfig.edges.goalCycle, edgeType: propertyType.options },
        ...goalProperties.value.map((e) => ({ isProperty: true, property: e })),
      ]);
    case updateConfig.model:
      return directUpdateProperties(t).map((prop) => ({
        isProperty: false,
        edge: prop.edgeName,
        edgeType: prop.type,
        label: prop.label,
      }));
    case goalActivityConfig.model:
      return directGoalActivityProperties(t).map((prop) => ({
        isProperty: false,
        edge: prop.edgeName,
        edgeType: prop.type,
        label: prop.label,
      }));
    default:
      return [];
  }
});

const yAxisGroupBy = ref(null);
const stackOptions = ref(null);
const excludeNullValues = ref(false);
const selectedInterval = ref(timeSeriesInterval.weekly);
const showExcludeNullValues = computed(() => canFillEmptyValues(xAxisGroupBy.value));

const isTimeSeriesMode = computed(() => TIME_SERIES_AGGREGATORS.includes(yAxisGroupBy.value?.aggregator));

const effectiveChartType = computed(() => {
  if (isTimeSeriesMode.value) {
    return gridPageChartType.line;
  }
  return chartType.value;
});

const timeSeriesIntervalOptions = [
  { text: t('gridPageChartEditor.daily'), value: timeSeriesInterval.daily },
  { text: t('gridPageChartEditor.weekly'), value: timeSeriesInterval.weekly },
  { text: t('gridPageChartEditor.monthly'), value: timeSeriesInterval.monthly },
];

const toggleExcludeNullValues = () => {
  excludeNullValues.value = !excludeNullValues.value;
};

const { userLang } = useLoggedInUser();
const goalDefaultProps = computed(() => createPropsList({
  properties: goalProperties.value,
  directProperties: goalChartProperties(t),
  userLang: userLang.value,
  model: goalConfig.model,
}));

const updateDefaultProps = computed(() => createPropsList({
  properties: [],
  directProperties: directUpdateProperties(t),
  userLang: userLang.value,
  model: updateConfig.model,
}));

const goalActivityDefaultProps = computed(() => createPropsList({
  properties: [],
  directProperties: directGoalActivityProperties(t),
  userLang: userLang.value,
  model: goalActivityConfig.model,
}));

const filterProps = computed(() => {
  switch (source.value) {
    case goalConfig.model:
      return goalDefaultProps.value;
    case updateConfig.model:
      return updateDefaultProps.value;
    case goalActivityConfig.model:
      return goalActivityDefaultProps.value;
    default:
      return [];
  }
});

const sources = [
  {
    text: t('gridPageChartEditor.goal'),
    icon: 'compass',
    value: goalConfig.model,
  },
  {
    text: t('gridPageChartEditor.checkin'),
    icon: 'history',
    value: updateConfig.model,
  },
  {
    text: t('gridPageChartEditor.goalActivity'),
    icon: 'retweet',
    value: goalActivityConfig.model,
  },
];

const init = () => {
  source.value = gridPageTileChart.value.source;
  chartType.value = gridPageTileChart.value.chartType;
  filter.value = gridPageTileChart.value.filter;
  selectedCycles.value = gridPageTileChart.value.goalCycles;
  yAxisGroupBy.value = gridPageTileChart.value.aggregationConfig.aggregation;
  stackOptions.value = { ...gridPageTileChart.value.aggregationConfig.stackOptions, customBucket: undefined };
  xAxisGroupBy.value = { ...gridPageTileChart.value.groupBy, customBucket: undefined };
  customBucket.value = { ...gridPageTileChart.value.groupBy.customBucket };
  stackCustomBucket.value = { ...gridPageTileChart.value.aggregationConfig.stackOptions.customBucket };
  excludeNullValues.value = gridPageTileChart.value.excludeNullValues ?? false;
  selectedInterval.value = gridPageTileChart.value.aggregationConfig.timeSeriesInterval ?? timeSeriesInterval.weekly;
};

const chartDataQuery = useChartDataQuery(toSave);

if (gridPageTileChart.value !== null) {
  init();
}

const findMatchingProperty = (currentValue, availableProperties) => {
  if (currentValue === null || currentValue === undefined) {
    return null;
  }
  return availableProperties.find((prop) => {
    if (currentValue.isProperty === true && prop.isProperty === true) {
      return currentValue.property?.uid === prop.property?.uid;
    }
    if (currentValue.isProperty === false && prop.isProperty === false) {
      return currentValue.edge === prop.edge;
    }
    return false;
  });
};

const getDefaultGroupBy = (availableProperties) => {
  const nonDateNumberProp = availableProperties.find((p) => {
    const type = p.isProperty === true ? p.property.type : p.edgeType;
    return type !== propertyType.date && type !== propertyType.number;
  });
  if (nonDateNumberProp !== undefined) {
    return { ...nonDateNumberProp, groupBy: chartGroupByOption.unique };
  }
  const firstProp = availableProperties[0];
  if (firstProp !== undefined) {
    const type = firstProp.isProperty === true ? firstProp.property.type : firstProp.edgeType;
    const groupBy = type === propertyType.date ? chartGroupByOption.day : chartGroupByOption.unique;
    return { ...firstProp, groupBy };
  }
  return { groupBy: chartGroupByOption.none };
};

watch(source, (newVal, oldVal) => {
  if (newVal === oldVal) {
    return;
  }
  filter.value = null;

  if (newVal !== goalConfig.model && isTimeSeriesMode.value) {
    yAxisGroupBy.value = { aggregator: aggregatorType.count };
  }

  // properties.value is already updated since source changed
  const newProperties = properties.value;

  const matchingXAxis = findMatchingProperty(xAxisGroupBy.value, newProperties);
  xAxisGroupBy.value = matchingXAxis !== null && matchingXAxis !== undefined
    ? { ...matchingXAxis, groupBy: xAxisGroupBy.value.groupBy }
    : getDefaultGroupBy(newProperties);

  const matchingYAxis = findMatchingProperty(yAxisGroupBy.value, newProperties);
  yAxisGroupBy.value = matchingYAxis !== null && matchingYAxis !== undefined
    ? { ...matchingYAxis, aggregator: yAxisGroupBy.value.aggregator }
    : { aggregator: aggregatorType.count };

  const matchingStack = findMatchingProperty(stackOptions.value, newProperties);
  stackOptions.value = matchingStack !== null && matchingStack !== undefined
    ? { ...matchingStack, groupBy: stackOptions.value.groupBy }
    : { groupBy: chartGroupByOption.none };
});

const isDirty = computed(() => !isEqualGridPageTileChart(toSave.value, gridPageTileChart.value));
</script>

<style scoped lang="scss">
$top-bar-height: 6rem;

.grid-page-chart-editor {
  display: grid;
  grid-template-rows: $top-bar-height 1fr;
  height: var(--viewport-height-100);
  overflow: hidden;

  ._top {
    flex: 0 0 4rem;
    border-bottom: 1px solid $border-color;
    display: flex;
    align-items: center;
    padding: 0 2rem;

    ._left {
      display: flex;
      overflow: hidden;

      ._divider {
        margin: 0 .6rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    ._right {
      margin-left: auto;
      display: flex;
      gap: .4rem;
      flex-shrink: 0;
    }

    @media (max-width: $screen-size-md) {
      padding: 0 1rem;

      ._left {
        min-width: 0;

        ._breadcrumbs,
        ._divider,
        ._description {
          display: none;
        }

        ._title {
          font-weight: $font-weight-semibold;
        }
      }

      ._right {
        ._discard-btn {
          display: none;
        }
      }
    }
  }

  ._bottom {
    display: grid;
    grid-template-columns: 1fr 30rem;
    height: 100%;
    overflow: hidden;

    ._left {
      display: grid;
      overflow: hidden;

      ._draggable-layout {
        overflow: auto;
      }

      ._chart-container {
        height: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        ._chart-content {
          height: 100%;
          width: 100%;
          display: flex;
          overflow: hidden;
        }

        &.-table {
          overflow: auto;

          ._chart-content {
            display: inline-table;
          }
        }
      }

      ._table-container {
        display: inline-table;
        min-width: 100%;
        padding-top: 2rem;
      }
    }

    ._right {
      padding: 2rem;
      border-left: 1px solid $border-color;
      overflow-y: auto;
    }

    @media (max-width: $screen-size-md) {
      grid-template-columns: 1fr;

      ._left {
        display: none;
      }

      ._right {
        border-left: none;
      }
    }
  }

  ._chart-type-selector {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    ._chart-btn {
      border: none;
      width: 4.2rem;
      height: 4.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 0 0 1px $input-border-color;

      ._icon-wrapper {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.-selected {
        box-shadow: 0 0 0 2px $primary-color;
      }

      &.-disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }

  ._user-grouping-hint {
    font-size: 1.2rem;
  }
}
</style>
